/**
 * CDR NA LINHA DA FATURA (auditoria do painel, 07/10/2026, decisão do Heitor).
 *
 * Palavras dele: "ter visual que a fatura PRECISA do CDR aplicado e marcar SE ESTÁ APLICADO OU NÃO". São dois
 * estados, e o primeiro é a condição do segundo:
 *  1. a fatura PRECISA de CDR? A assinatura dela tem "Incluir tarifas de consumo (CDR) na fatura" ligado
 *     (`contracts.includeUsageItems`);
 *  2. e o CDR FOI aplicado? A fatura tem consumo processado (`usageItems`, ou as listas de entrada).
 *
 * Fatura que não precisa: NADA. É a maioria (130 de 145 em outubro/2026), e qualquer marca nela seria ruído
 * para servir as 15 que precisam.
 *
 * ⚠️ O estado "precisa e NÃO foi aplicado" nunca aconteceu até 11/2026, e por um defeito: a geração mensal
 * materializava o consumo a partir de `contracts.usageMinutes`, e toda fatura nascia "aplicada". Com a decisão A do
 * Heitor (03/11/2026, "fica sem os minutos, e só libera aprovação quando processar o CDR"), a geração deixa de
 * materializar, e a fatura de dezembro nasce PENDENTE até alguém processar o CDR. O backend recusa a aprovação
 * nesse estado (hold calculado no finalize, `helpers/cdr-na-fatura.util`), com a mesma regra desta função; a saída é
 * liberar com motivo (`releaseNote`). Pendente é AVISO; o aplicado é quieto, informação e não alarme.
 *
 * "Aplicado" é consumo PROCESSADO, não consumo cobrado: há tarifa zero (minutos com valor R$ 0,00). O sinal é
 * o consumo, e não o registro de chamada anexado (`cdrRecords`), que a lista não traz e o Heitor não pediu.
 */

export interface ItemDeConsumo {
  minutes?: number | string | null;
  totalValue?: number | string | null;
}

export interface FaturaComConsumo {
  status?: string;
  usageItems?: ItemDeConsumo[] | null;
  usageItemsEntrada?: ItemDeConsumo[] | null;
  usageItemsEntradaNormal?: ItemDeConsumo[] | null;
}

export interface EstadoDoCdr {
  aplicado: boolean;
  tom: 'quieto' | 'aviso';
  texto: string;
  titulo: string;
}

/** A frase da trava, no selo, no Aprovar e no lote: a mesma que o servidor aplica no finalize. */
export const MOTIVO_DO_CDR_PENDENTE = 'Aprovação recusada até processar o CDR';

const brl = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `precisa` = a assinatura da fatura tem o CDR ligado. Sem assinatura (consolidada, avulsa), não precisa. */
export function estadoDoCdr(inv: FaturaComConsumo, precisa: boolean): EstadoDoCdr | null {
  if (!precisa || inv.status === 'Cancelada') return null;
  const itens = [...(inv.usageItems || []), ...(inv.usageItemsEntrada || []), ...(inv.usageItemsEntradaNormal || [])];
  if (itens.length === 0) {
    return {
      aplicado: false,
      tom: 'aviso',
      texto: 'CDR pendente',
      titulo: `A assinatura inclui as tarifas de consumo (CDR) na fatura, e esta fatura não tem consumo processado. ${MOTIVO_DO_CDR_PENDENTE}.`,
    };
  }
  const minutos = itens.reduce((s, i) => s + (Number(i.minutes) || 0), 0);
  const valor = itens.reduce((s, i) => s + (Number(i.totalValue) || 0), 0);
  return {
    aplicado: true,
    tom: 'quieto',
    texto: 'CDR aplicado',
    titulo: `Consumo processado: ${itens.length} ${itens.length === 1 ? 'tarifa' : 'tarifas'}, ${Math.round(minutos)} min, R$ ${brl(valor)}.`,
  };
}

/** A fatura em Rascunho que o servidor recusa aprovar pelo CDR: precisa e não tem consumo processado. */
export function cdrPendenteNaAprovacao(inv: FaturaComConsumo, precisa: boolean): boolean {
  return inv.status === 'Rascunho' && estadoDoCdr(inv, precisa)?.aplicado === false;
}
